// Утилита для проверки и захвата городов в конце раунда
import {getPlayerByColor} from "../players/getPlayerByColor";
import {getPlayerById} from "../players/getPlayerById";
import {COMMON_BUILDING_TOKENS} from "../../constants/tokens";
import {calcCityWinPoints} from "./winPoints/calcCityWinPoints";

/**
 * Проверить, захвачен ли город противником
 * Город считается захваченным если:
 * - На любом квартале (обычном или центральном) города есть отряд противника
 * - При этом ни на одном квартале города нет отрядов владельца города
 *
 * @param {Object} city - Город из citiesCache
 * @param {Array} units - Массив отрядов
 * @param {Object} owner - Игрок-владелец города
 * @param {Object} players - Все игроки
 * @returns {string|null} - Цвет захватившего игрока или null если город не захвачен
 */
export const checkCityCapture = (city, units, owner, players) => {
  // Собираем все кварталы города (центральный + базовые)
  const cityHexKeys = [city.centralQuarter, ...(city[COMMON_BUILDING_TOKENS.QUARTER] || [])];

  const unitsOnQuarters = units.filter(unit =>
    cityHexKeys.some(hexKey =>
      unit.hexKey === hexKey
    )
  );

  const ownerUnits = unitsOnQuarters.filter(unit => unit.ownerId === owner.id);
  if (ownerUnits.length > 0) {
    // Город нельзя захватить, если на любом его квартале есть отряд игрока-владельца города
    return null;
  }

  const enemyUnits = unitsOnQuarters.filter(unit => unit.ownerId !== owner.id);
  if (enemyUnits.length === 0) {
    // Город не захватывается, так как на его кварталах нет отрядов противников.
    return null;
  }

  const enemyPlayerIdsSet = new Set();
  enemyUnits.forEach(unit => {
    enemyPlayerIdsSet.add(unit.ownerId)
  });

  const enemyPlayerIds = [...enemyPlayerIdsSet];
  if (enemyPlayerIds.length > 1) {
    // Захват города не происходит, если на его кварталах есть отряды 2 и более вражеских игроков.
    // Даже если отрядов самого игрока-владельца нет на кварталах города.
    return null;
  }

  return getPlayerById(players, enemyPlayerIds[0]);
};

/**
 * Выполнить захват городов и вернуть обновленный hexes
 *
 * @param {Object} citiesCache - Кэш городов
 * @param {Object} hexes - Состояние поля
 * @param {Array} players - Массив игроков
 * @param {Array} units - Массив отрядов
 * @returns {Object} - { hexes: Object, capturedCities: Array }
 */
export const processCityCaptures = (citiesCache, hexes, players, units) => {
  let newHexes = {...hexes};
  const capturedCities = [];

  // Для каждого цвета в citiesCache проверяем города
  for (const [color, cities] of Object.entries(citiesCache)) {
    if (!cities || !Array.isArray(cities)) continue;

    const owner = getPlayerByColor(players, color);

    for (const city of cities) {
      // Проверяем, захвачен ли город
      const newOwner = checkCityCapture(city, units, owner, players);

      if (newOwner) {
        // Захватываем город - меняем центральный квартал на цвет захватившего
        const centralQuarterHexKey = city.centralQuarter;
        const currentToken = newHexes[centralQuarterHexKey];

        if (currentToken && currentToken.startsWith('central_quarter_')) {
          // Заменяем на центральный квартал нового владельца
          newHexes[centralQuarterHexKey] = `central_quarter_${newOwner.color}`;

          const cityWinPoints = calcCityWinPoints(city);

          capturedCities.push({
            city,
            cityWinPoints,
            fromPlayer: owner,
            toPlayer: newOwner,
            centralQuarterHexKey,
          });
        }
      }
    }
  }

  return {hexes: newHexes, capturedCities};
};